import Papa from 'papaparse';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  FiChevronDown,
  FiChevronUp,
  FiPlus,
  FiRepeat,
  FiUploadCloud,
  FiRefreshCw,
} from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';
import { AudienceMember } from '../../features/audience/audienceSlice';
import { BASE_URL } from '../../utils/constants';
import { getAuthContext } from '../../utils/jwt';
import AudienceTable from './AudienceTable';
import ChangeSegmentModal from './ChangeSegmentModal';
import EditTableFormatModal from './EditTableFormatModal';
import ExportModal from './ExportModal';
import ExportPreviewModal from './ExportPreviewModal';
import ImportAudienceModal from './ImportAudienceModal';
import SnapshotStatusModal from './SnapshotStatusModal';

// const FiEdit3Component = FiEdit3 as unknown as React.ComponentType<{
//   size?: number | string;
//   className?: string;
// }>;
const FiChevronDownComponent = FiChevronDown as unknown as React.ComponentType<{
  size?: number | string;
  className?: string;
}>;
const FiChevronUpComponent = FiChevronUp as unknown as React.ComponentType<{
  size?: number | string;
  className?: string;
}>;
const FiPlusComponent = FiPlus as unknown as React.ComponentType<{
  size?: number | string;
  className?: string;
}>;
const FiUploadCloudComponent = FiUploadCloud as unknown as React.ComponentType<{
  size?: number | string;
  className?: string;
}>;
const FiRepeatComponent = FiRepeat as unknown as React.ComponentType<{
  size?: number | string;
  className?: string;
}>;
const FiRefreshCwComponent = FiRefreshCw as unknown as React.ComponentType<{
  size?: number | string;
  className?: string;
}>;
// const FiXComponent = FiX as unknown as React.ComponentType<{
//   size?: number | string;
//   className?: string;
// }>;
// const FiChevronLeftComponent = FiChevronLeft as unknown as React.ComponentType<{
//   size?: number | string;
//   className?: string;
// }>;
// const FiChevronRightComponent = FiChevronRight as unknown as React.ComponentType<{
//   size?: number | string;
//   className?: string;
// }>;

interface FieldConfig {
  name: string;
  filter: boolean;
}

interface ApiSegment {
  id: string;
  name: string;
  type: string;
  description?: string | null;
  rulesJson: any[];
}

interface ApiContact {
  id: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  dob?: string;
  gender?: string;
  address?: string;
  tags?: string[];
  segments?: string[] | null;
  contactStatus?: string;
  status?: string;
  createdAt?: string;
  updatedAt?: string;
  lastActivityAt?: string | null;
  lastEmailOpenedAt?: string | null;
  lastClickAt?: string | null;
  engagementScore?: number | null;
  deliveredCount?: number | null;
  openCount?: number | null;
  clickCount?: number | null;
  bounceCount?: number | null;
}

interface SegmentWithCount extends ApiSegment {
  memberCount?: number;
  loadingCount?: boolean;
}

const getStatusColor = (status: string) => {
  switch (status) {
    case 'Subscribed':
    case 'SUBSCRIBED':
      return 'bg-green-100 text-green-600';
    case 'Unsubscribed':
    case 'UNSUBSCRIBED':
      return 'bg-red-100 text-red-600';
    default:
      return 'bg-gray-100 text-gray-600';
  }
};

const Audience: React.FC = () => {
  const navigate = useNavigate();
  const fileInputRef = React.useRef<HTMLInputElement | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // API States
  const [contacts, setContacts] = useState<ApiContact[]>([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalCount, setTotalCount] = useState(0);

  // Segment members API states
  const [segmentMembers, setSegmentMembers] = useState<ApiContact[]>([]);
  const [loadingSegmentMembers, setLoadingSegmentMembers] = useState(false);
  const [segmentTotalCount, setSegmentTotalCount] = useState(0);

  // Frontend pagination state for filtered results
  const [frontendCurrentPage, setFrontendCurrentPage] = useState(0);
  const frontendItemsPerPage = 6;

  const [activeSegment, setActiveSegment] = useState('All');
  const [activeSegmentId, setActiveSegmentId] = useState<string | null>(null);
  const [sortConfig, setSortConfig] = useState<{
    key: keyof AudienceMember;
    direction: 'asc' | 'desc';
  } | null>(null);
  const [selected, setSelected] = useState<string[]>([]);

  // Refresh state
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Export state
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [pendingExportType, setPendingExportType] = useState<'all' | 'segment' | null>(null);

  // Export Preview state
  const [isExportPreviewModalOpen, setIsExportPreviewModalOpen] = useState(false);
  const [currentExportId, setCurrentExportId] = useState<string | null>(null);

  // Snapshot state
  const [isCreatingSnapshot, setIsCreatingSnapshot] = useState(false);
  const [isSnapshotStatusModalOpen, setIsSnapshotStatusModalOpen] = useState(false);
  const [currentSnapshotId, setCurrentSnapshotId] = useState<string | null>(null);
  const [currentSegmentId, setCurrentSegmentId] = useState<string | null>(null);

  // Other modals state
  const [isImportOpen, setIsImportOpen] = useState(false);
  const [isChangeOpen, setIsChangeOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [nameFilter, setNameFilter] = useState<string>('');

  // State for segments dropdown
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [segmentsDisplayLimit, setSegmentsDisplayLimit] = useState(6);
  const segmentsIncrement = 6;

  const [fieldConfigs, setFieldConfigs] = useState<FieldConfig[]>([
    { name: 'Name & Email', filter: true },
    { name: 'DOB', filter: false },
    { name: 'Gender', filter: true },
    { name: 'Contact', filter: true },
    { name: 'Tag', filter: true },
    { name: 'Last Activities', filter: true },
    { name: 'Engage Score', filter: true },
    { name: 'Status', filter: true },
  ]);

  const [apiSegments, setApiSegments] = useState<SegmentWithCount[]>([]);
  const [loadingSegments, setLoadingSegments] = useState(false);

  const itemsPerPage = 6;

  const isInitialLoadDone = useRef(false);
  // const isPageChangeInProgress = useRef(false);
  const fetchInProgress = useRef(false);
  const lastFetchedPage = useRef<number>(-1);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchSegments = useCallback(async () => {
    const { token, tenantId, userId } = getAuthContext();
    if (!token) return;

    setLoadingSegments(true);

    try {
      const response = await fetch(`${BASE_URL}/v1/audience-insights/segments?page=0&size=1000`, {
        method: 'GET',
        headers: {
          'X-Tenant-Id': tenantId,
          'X-User-Id': userId || '',
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const data = await response.json();
      const segments = data.content || [];

      // Initialize segments with loading state for counts
      const segmentsWithCount = segments.map((seg: ApiSegment) => ({
        ...seg,
        memberCount: undefined,
        loadingCount: true
      }));

      setApiSegments(segmentsWithCount);

      // Fetch counts for each segment
      for (const seg of segments) {
        fetchSegmentCount(seg.id);
      }
    } catch (error) {
      // console.error('❌ Failed to fetch segments:', error);
      toast.error('Failed to load segments');
    } finally {
      setLoadingSegments(false);
    }
  }, []);

  // ==================== FETCH SEGMENT COUNT ====================
  const fetchSegmentCount = useCallback(async (segmentId: string) => {
    const { token, tenantId, userId } = getAuthContext();
    if (!token || !segmentId) return;

    try {
      const response = await fetch(
        `${BASE_URL}/v1/audience-insights/segments/${segmentId}/count`,
        {
          method: 'GET',
          headers: {
            'X-Tenant-Id': tenantId,
            'X-User-Id': userId || '',
            'Authorization': `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const data = await response.json();
      const count = data.count || data.totalCount || 0;

      // Update the segment with its count
      setApiSegments(prevSegments =>
        prevSegments.map(seg =>
          seg.id === segmentId
            ? { ...seg, memberCount: count, loadingCount: false }
            : seg
        )
      );
    } catch (error) {
      // console.error(`❌ Failed to fetch count for segment ${segmentId}:`, error);
      // Set count to 0 on error
      setApiSegments(prevSegments =>
        prevSegments.map(seg =>
          seg.id === segmentId
            ? { ...seg, memberCount: 0, loadingCount: false }
            : seg
        )
      );
    }
  }, []);

  // ==================== FETCH SEGMENT MEMBERS ====================
  const fetchSegmentMembers = useCallback(async (segmentId: string) => {
    const { token, tenantId, userId } = getAuthContext();
    if (!token || !segmentId) return;

    setLoadingSegmentMembers(true);

    try {
      const response = await fetch(
        `${BASE_URL}/v1/audience-insights/segments/${segmentId}/members?size=1000`,
        {
          method: 'GET',
          headers: {
            'X-Tenant-Id': tenantId,
            'X-User-Id': userId || '',
            'Authorization': `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const data = await response.json();
      const members = data.content || [];
      const totalCountData = data.totalCount || 0;

      setSegmentMembers(members);
      setSegmentTotalCount(totalCountData);
    } catch (error) {
      toast.error('Failed to load segment members');
      setSegmentMembers([]);
      setSegmentTotalCount(0);
    } finally {
      setLoadingSegmentMembers(false);
    }
  }, []);

  // ==================== FETCH CONTACTS (PAGINATED) ====================
  const fetchContacts = useCallback(async (page: number = 0) => {
    const { token, tenantId, userId } = getAuthContext();
    if (!token) return;

    // Prevent concurrent fetches
    if (fetchInProgress.current) {
      return;
    }

    // Prevent fetching if page is out of bounds
    if (page < 0) {
      return;
    }

    // Skip if we already fetched this page
    if (lastFetchedPage.current === page) {
      return;
    }

    fetchInProgress.current = true;
    setLoading(true);

    try {

      const response = await fetch(
        `${BASE_URL}/v1/audience-insights/contacts?size=${itemsPerPage}&page=${page}`,
        {
          method: 'GET',
          headers: {
            'X-Tenant-Id': tenantId,
            'X-User-Id': userId || '',
            'Authorization': `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const data = await response.json();
      const contactsData = data.contacts?.content || data.content || [];
      const newTotalPages = data.contacts?.totalPages || data.totalPages || 0;
      const newTotalCount = data.contacts?.totalCount || data.totalCount || 0;

      setContacts(contactsData);
      setTotalPages(newTotalPages);
      setTotalCount(newTotalCount);
      lastFetchedPage.current = page;

    } catch (error) {
      // console.error('❌ Failed to fetch contacts:', error);
      toast.error('Failed to load audience');
      setContacts([]);
    } finally {
      setLoading(false);
      fetchInProgress.current = false;
    }
  }, [itemsPerPage]);

  // ==================== INITIAL LOAD ====================
  useEffect(() => {
    if (!isInitialLoadDone.current) {
      fetchSegments();
      fetchContacts(0);
      isInitialLoadDone.current = true;
    }
  }, [fetchSegments, fetchContacts]);

  useEffect(() => {
    if (activeSegment !== 'All') {
      // Find the selected segment
      const selectedSegment = apiSegments.find(seg => seg.name === activeSegment);
      if (selectedSegment) {
        setActiveSegmentId(selectedSegment.id);
        setFrontendCurrentPage(0);
        // Fetch members for this segment using the backend API
        fetchSegmentMembers(selectedSegment.id);
      }
    } else {
      setActiveSegmentId(null);
      setSegmentMembers([]);
      setSegmentTotalCount(0);
      // Clear selections when switching to All
      setSelected([]);
    }
  }, [activeSegment, apiSegments, fetchSegmentMembers]);

  // ==================== HANDLERS ====================
  const uniqueSegments = useMemo(() => {
    const seenNames = new Set<string>();
    const unique: SegmentWithCount[] = [];

    apiSegments.forEach((seg) => {
      if (!seenNames.has(seg.name)) {
        seenNames.add(seg.name);
        unique.push(seg);
      }
    });
    return unique;
  }, [apiSegments]);

  // Get displayed segments based on limit
  const displayedSegments = useMemo(() => {
    return uniqueSegments.slice(0, segmentsDisplayLimit);
  }, [uniqueSegments, segmentsDisplayLimit]);

  // Check if there are more segments to show
  const hasMoreSegments = segmentsDisplayLimit < uniqueSegments.length;

  // Handle show more/less
  const handleToggleSegments = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (hasMoreSegments) {
      setSegmentsDisplayLimit(prev => prev + segmentsIncrement);
    } else {
      setSegmentsDisplayLimit(segmentsIncrement);
    }
  };

  const handleSegmentSelect = (value: string) => {
    setActiveSegment(value);
    setIsDropdownOpen(false);
    // Reset segments display limit when a segment is selected
    setSegmentsDisplayLimit(6);
  };

  // ==================== REFRESH HANDLER ====================
  const handleRefresh = useCallback(async () => {
    if (isRefreshing || loading || loadingSegmentMembers) return;

    setIsRefreshing(true);
    // Reset lastFetchedPage to force refetch
    lastFetchedPage.current = -1;

    try {
      if (activeSegment === 'All') {
        // Refresh contacts (current page) + segments
        await Promise.all([
          fetchContacts(currentPage),
          fetchSegments(),
        ]);
      } else if (activeSegmentId) {
        // Refresh segment members + segments
        await Promise.all([
          fetchSegmentMembers(activeSegmentId),
          fetchSegments(),
        ]);
      } else {
        await fetchSegments();
      }
    } catch (err) {
      // Individual fetch functions handle their own error toasts
    } finally {
      setIsRefreshing(false);
    }
  }, [
    isRefreshing,
    loading,
    loadingSegmentMembers,
    activeSegment,
    activeSegmentId,
    currentPage,
    fetchContacts,
    fetchSegments,
    fetchSegmentMembers,
  ]);

  const displayContacts = useMemo(() => {
    if (activeSegment !== 'All') {
      // Use segment members from the API
      return segmentMembers;
    }
    return contacts;
  }, [activeSegment, contacts, segmentMembers]);

  const mappedAudience = useMemo(() => {
    return displayContacts.map((c: ApiContact): AudienceMember => {
      const status = c.status || c.contactStatus || 'SUBSCRIBED';
      const formattedStatus = status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();

      let lastActivity = 'Just now';
      if (c.lastActivityAt) {
        try {
          lastActivity = new Date(c.lastActivityAt).toLocaleDateString();
        } catch (e) {
          lastActivity = 'Just now';
        }
      }

      return {
        id: c.id,
        name: `${c.firstName || ''} ${c.lastName || ''}`.trim() || 'N/A',
        email: c.email || '',
        segment: activeSegment === 'All' ? 'General' : activeSegment,
        tags: c.tags || [],
        lastActivity,
        engageScore: c.engagementScore ?? 0, // If null or undefined, use 0
        status: formattedStatus,
        phone: c.phone || '',
        dob: c.dob || '',
        gender: c.gender || '',
        address: c.address || '',
        postalCode: '',
        segments: c.segments || [],
      } as AudienceMember;
    });
  }, [displayContacts, activeSegment]);

  const filteredAudience = useMemo(() => {
    let filtered = mappedAudience;

    if (nameFilter.trim()) {
      filtered = filtered.filter(
        (a) =>
          a.name.toLowerCase().includes(nameFilter.toLowerCase()) ||
          a.email.toLowerCase().includes(nameFilter.toLowerCase())
      );
    }

    return filtered;
  }, [mappedAudience, nameFilter]);

  const sortedAudience = useMemo(() => {
    if (!sortConfig) return filteredAudience;
    return [...filteredAudience].sort((a, b) => {
      const valA = (a[sortConfig.key] as any)?.toString().toLowerCase() ?? '';
      const valB = (b[sortConfig.key] as any)?.toString().toLowerCase() ?? '';
      if (valA < valB) return sortConfig.direction === 'asc' ? -1 : 1;
      if (valA > valB) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  }, [filteredAudience, sortConfig]);

  // ==================== GET PAGINATED DATA ====================
  const getPaginatedData = useMemo(() => {
    if (activeSegment !== 'All') {
      // Use frontend pagination for segment filtering
      const startIndex = frontendCurrentPage * frontendItemsPerPage;
      const endIndex = startIndex + frontendItemsPerPage;
      return sortedAudience.slice(startIndex, endIndex);
    }
    // For "All", use the backend paginated data
    return sortedAudience;
  }, [sortedAudience, activeSegment, frontendCurrentPage, frontendItemsPerPage]);

  // ==================== GET TOTAL PAGES ====================
  const getTotalPages = useMemo(() => {
    if (activeSegment !== 'All') {
      return Math.ceil(sortedAudience.length / frontendItemsPerPage);
    }
    return totalPages;
  }, [sortedAudience, activeSegment, frontendItemsPerPage, totalPages]);

  // ==================== GET TOTAL COUNT ====================
  const getTotalCount = useMemo(() => {
    if (activeSegment !== 'All') {
      return sortedAudience.length;
    }
    return totalCount;
  }, [sortedAudience, activeSegment, totalCount]);

  // ==================== CALCULATE START AND END ITEMS ====================
  const startItem = useMemo(() => {
    if (activeSegment !== 'All') {
      return frontendCurrentPage * frontendItemsPerPage + 1;
    }
    return currentPage * itemsPerPage + 1;
  }, [activeSegment, frontendCurrentPage, frontendItemsPerPage, currentPage, itemsPerPage]);

  const endItem = useMemo(() => {
    if (activeSegment !== 'All') {
      return Math.min((frontendCurrentPage + 1) * frontendItemsPerPage, sortedAudience.length);
    }
    return Math.min((currentPage + 1) * itemsPerPage, totalCount);
  }, [activeSegment, frontendCurrentPage, frontendItemsPerPage, sortedAudience.length, currentPage, itemsPerPage, totalCount]);

  // ==================== PAGE CHANGE HANDLER ====================
  const handlePageChange = useCallback((newPage: number) => {
    // If a specific segment is selected, use frontend pagination
    if (activeSegment !== 'All') {
      const totalFilteredPages = Math.ceil(sortedAudience.length / frontendItemsPerPage);
      if (newPage < 0 || newPage >= totalFilteredPages) {
        return;
      }
      if (newPage === frontendCurrentPage) {
        return;
      }
      setFrontendCurrentPage(newPage);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    // For "All", use backend pagination
    if (newPage < 0 || newPage >= totalPages) {
      return;
    }

    if (newPage === currentPage) {
      return;
    }

    if (loading || fetchInProgress.current) {
      return;
    }

    setCurrentPage(newPage);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentPage, totalPages, loading, activeSegment, sortedAudience, frontendCurrentPage, frontendItemsPerPage]);

  // ==================== EFFECT FOR PAGE CHANGES ====================
  useEffect(() => {
    // Only fetch from backend when "All" is selected
    if (activeSegment !== 'All') {
      // Don't fetch from backend for segment filtering
      return;
    }

    // Skip if initial load hasn't completed
    if (!isInitialLoadDone.current) return;

    // Skip if we're already fetching
    if (fetchInProgress.current) return;

    // Skip if page is invalid
    if (currentPage < 0) return;

    // Skip if we already fetched this page
    if (lastFetchedPage.current === currentPage) return;

    // Fetch the new page
    fetchContacts(currentPage);
  }, [currentPage, fetchContacts, activeSegment]);

  // ==================== EFFECT FOR FILTERS ====================
  useEffect(() => {
    if (isInitialLoadDone.current) {
      if (activeSegment !== 'All') {
        // Reset frontend pagination when segment changes
        setFrontendCurrentPage(0);
      } else if (currentPage !== 0) {
        // Reset backend pagination when filter changes
        setCurrentPage(0);
      }
    }
  }, [nameFilter, activeSegment]);

  const handleNameFilterApply = (filterValue: string) => {
    setNameFilter(filterValue);
  };

  const handleNameFilterClear = () => {
    setNameFilter('');
  };

  // ==================== EXPORT FUNCTIONS ====================

  // Determine source type based on context
  const getSourceType = useCallback((): { sourceType: string; segmentId: string | null } => {
    // Case 1: "All" selected
    if (activeSegment === 'All') {
      return { sourceType: 'ALL', segmentId: null };
    }

    // Case 2: Segment selected with manual selections
    if (selected.length > 0) {
      return { sourceType: 'MANUAL', segmentId: activeSegmentId };
    }

    // Case 3: Segment selected with no manual selections
    return { sourceType: 'SEGMENT', segmentId: activeSegmentId };
  }, [activeSegment, activeSegmentId, selected]);

  // Create export request and show preview modal
  const exportCSV = async (exportType: 'all' | 'segment') => {
    const { token, tenantId, userId } = getAuthContext();
    if (!token) {
      toast.error('Authentication required');
      return;
    }

    // Validate based on export type
    if (exportType === 'all') {
      if (totalCount === 0) {
        toast.warning('No contacts available to export');
        return;
      }
    } else {
      if (selected.length === 0 && segmentTotalCount === 0) {
        toast.warning('No contacts available to export');
        return;
      }
    }

    try {

      // Get source type and segment ID
      const { sourceType, segmentId } = getSourceType();

      // Prepare request body - always CSV format
      const requestBody: any = {
        sourceType: sourceType,
        format: 'csv'
      };

      // Only add segmentId if sourceType is not 'ALL'
      if (sourceType !== 'ALL' && segmentId) {
        requestBody.segmentId = segmentId;
      } else {
        requestBody.segmentId = null;
      }

      // Only add contactIds for MANUAL source type
      if (sourceType === 'MANUAL' && selected.length > 0) {
        requestBody.contactIds = selected;
      }


      const response = await fetch(`${BASE_URL}/v1/audience-insights/exports`, {
        method: 'POST',
        headers: {
          'X-Tenant-Id': tenantId,
          'X-User-Id': userId || '',
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();
      const exportId = data.exportId;
      const snapshotId = data.snapshotId;
      const segmentIdFromResponse = data.segmentId;

      if (!exportId) {
        throw new Error('No export ID received');
      }



      // Close the export modal and open the preview modal
      setIsExportModalOpen(false);
      setCurrentExportId(exportId);
      setCurrentSnapshotId(snapshotId);
      setCurrentSegmentId(segmentIdFromResponse);
      setIsExportPreviewModalOpen(true);

    } catch (error) {
      // console.error('❌ Failed to create export:', error);
      toast.error('Failed to initiate export. Please try again.');
    }
  };

  // Download the export file
  const handleDownloadExport = async () => {
    if (!currentExportId) return;

    const { token, tenantId, userId } = getAuthContext();
    if (!token) return;

    try {

      const response = await fetch(`${BASE_URL}/v1/audience-insights/exports/${currentExportId}/download`, {
        method: 'GET',
        headers: {
          'X-Tenant-Id': tenantId,
          'X-User-Id': userId || '',
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      // Get the blob from response
      const blob = await response.blob();

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `audience_export_${new Date().toISOString()}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success('CSV exported successfully!');

      // Clear selection after successful export (only for manual exports)
      if (selected.length > 0) {
        setSelected([]);
      }

      // Close the preview modal
      setIsExportPreviewModalOpen(false);
      setCurrentExportId(null);
      setCurrentSnapshotId(null);
      setCurrentSegmentId(null);

    } catch (error) {
      // console.error('❌ Failed to download export:', error);
      toast.error('Failed to download exported file. Please try again.');
    }
  };

  // Export handlers with modal
  const handleExportAllClick = () => {
    if (totalCount === 0) {
      toast.warning('No contacts available to export');
      return;
    }
    setPendingExportType('all');
    setIsExportModalOpen(true);
  };

  const handleExportSegmentClick = () => {
    if (segmentTotalCount === 0) {
      toast.warning('No contacts available to export');
      return;
    }
    setPendingExportType('segment');
    setIsExportModalOpen(true);
  };

  // Actual export function that gets called after modal confirmation
  const handleExportConfirm = () => {
    if (pendingExportType) {
      exportCSV(pendingExportType);
    }
  };

  // Snapshot handler - creates a snapshot preview
  const handleSnapshot = async () => {
    try {
      setIsCreatingSnapshot(true);
      toast.info('Creating snapshot preview...');

      const { token, tenantId, userId } = getAuthContext();
      if (!token) {
        toast.error('Authentication required');
        setIsCreatingSnapshot(false);
        return;
      }

      // Get source type and segment ID
      const { sourceType, segmentId } = getSourceType();

      // For ALL source type, we need to use a different approach
      // The API expects a segmentId in the URL, so we need to handle ALL differently
      if (sourceType === 'ALL') {
        toast.warning('Snapshot preview is only available for specific segments');
        setIsCreatingSnapshot(false);
        return;
      }

      // Ensure we have a segmentId for snapshot
      if (!segmentId) {
        toast.error('No segment selected for snapshot');
        setIsCreatingSnapshot(false);
        return;
      }


      // Format date with 9 decimal places (nanoseconds precision)
      const formatDispatchedAt = (date: Date): string => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        const seconds = String(date.getSeconds()).padStart(2, '0');

        // Get milliseconds and pad to 9 digits (nanoseconds)
        const milliseconds = date.getMilliseconds();
        // Generate 9 digits: milliseconds (3 digits) + 6 zeros
        const nanoseconds = String(milliseconds).padEnd(3, '0') + '000000';

        return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}.${nanoseconds}Z`;
      };

      // Prepare request body based on source type
      let requestBody: any = {
        sourceType: sourceType,
        reason: 'PREVIEW',
        campaignId: 'campaign_Id',
        dispatchedAt: formatDispatchedAt(new Date())
      };

      // For MANUAL source type, include segmentId and contactIds
      if (sourceType === 'MANUAL') {
        requestBody.segmentId = segmentId;
        requestBody.contactIds = selected;
      } else if (sourceType === 'SEGMENT') {
        requestBody.segmentId = segmentId;
      }



      const response = await fetch(
        `${BASE_URL}/v1/audience-insights/segments/${segmentId}/snapshots`,
        {
          method: 'POST',
          headers: {
            'X-Tenant-Id': tenantId,
            'X-User-Id': userId || '',
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(requestBody),
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        // console.error('❌ Snapshot API error response:', errorData);
        throw new Error(`HTTP ${response.status}: ${JSON.stringify(errorData)}`);
      }

      const data = await response.json();

      // Store the snapshot ID and segment ID
      setCurrentSnapshotId(data.snapshotId);
      setCurrentSegmentId(segmentId);

      // Close the export modal and open the status modal
      setIsExportModalOpen(false);
      setIsSnapshotStatusModalOpen(true);

      toast.success('Snapshot created! Checking status...');

    } catch (error) {
      // console.error('❌ Failed to create snapshot:', error);
      toast.error('Failed to create snapshot preview. Please try again.');
    } finally {
      setIsCreatingSnapshot(false);
    }
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      Papa.parse(file, {
        header: true,
        complete: () => toast.success('File uploaded successfully'),
        error: () => toast.error('Failed to parse CSV file'),
      });
    }
  };

  const handleSaveFields = (updatedConfigs: FieldConfig[]) => {
    setFieldConfigs(updatedConfigs);
  };

  const tableFields = useMemo(() => fieldConfigs.map((config) => config.name), [fieldConfigs]);

  const refreshContactsWithDelay = useCallback(() => {
    // Reset lastFetchedPage to force a refetch
    lastFetchedPage.current = -1;
    setTimeout(() => {
      fetchContacts(currentPage);
      if (activeSegment !== 'All' && activeSegmentId) {
        fetchSegmentMembers(activeSegmentId);
      }
    }, 500);
  }, [currentPage, fetchContacts, activeSegment, activeSegmentId, fetchSegmentMembers]);

  // const segmentContactCount = useMemo(() => {
  //   if (activeSegment === 'All') {
  //     return totalCount;
  //   }
  //   return filteredAudience.length;
  // }, [activeSegment, filteredAudience, totalCount]);

  // Determine if pagination should be shown
  const showPagination = activeSegment !== 'All'
    ? getTotalPages > 1
    : totalPages > 1;

  // Get current page for display
  const currentPageDisplay = activeSegment !== 'All' ? frontendCurrentPage : currentPage;
  const totalPagesDisplay = activeSegment !== 'All' ? getTotalPages : totalPages;

  const isLoading = activeSegment !== 'All' ? loadingSegmentMembers : loading;

  const selectedSegmentLabel = useMemo(() => {
    if (activeSegment === 'All') return 'All';
    const segment = uniqueSegments.find(seg => seg.name === activeSegment);
    return segment ? segment.name : 'All';
  }, [activeSegment, uniqueSegments]);

  // Determine if checkboxes should be shown
  const showCheckboxes = activeSegment !== 'All';

  const handleAddAudience = () => {
    navigate('/audience/segments/add', {
      state: { step: 1, fields: [] },
    });
  };

  return (
    <div className="p-2">
      <div className="bg-gray-50 flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-[#3F5265] text-[24px]">Audience</h2>
          <p className="text-[#657584] text-[16px]">Manage your audience</p>
        </div>
        <div className="flex items-center gap-3">
          <input type="file" accept=".csv" ref={fileInputRef} style={{ display: 'none' }} onChange={handleFileUpload} />
          <button
            className="inline-flex items-center gap-2 px-4 py-2 rounded-md border border-orange-500 text-orange-500 bg-white hover:bg-orange-50 text-sm"
            onClick={() => setIsImportOpen(true)}
          >
            <FiUploadCloudComponent /> Import
          </button>
          <button
            className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-orange-500 text-white hover:bg-orange-600 text-sm"
            onClick={handleAddAudience}
          >
            <FiPlusComponent /> Add Audience
          </button>
          {/* <button
            className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-orange-500 text-white hover:bg-orange-600 text-sm"
            onClick={() => navigate('/audience/segments')}
          >
            <FiPlusComponent /> Add Segment
          </button> */}
        </div>
      </div>

      <div className="bg-white rounded-md border border-gray-200 shadow-sm mt-6">
        <div className="flex justify-between items-center px-6 pt-4">
          <div className="flex items-center space-x-4">
            <span className="text-[#3F5265] text-[16px] font-semibold">Segments</span>

            <div className="relative w-[200px]" ref={dropdownRef}>
              <button
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                className="w-[100%] border rounded-md px-3 py-2 text-sm text-gray-700 bg-white flex items-center justify-between min-w-[150px] hover:bg-gray-50"
                disabled={loadingSegments}
              >
                <span>{selectedSegmentLabel}</span>
                <FiChevronDownComponent className={`ml-2 transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`} size={16} />
              </button>

              {isDropdownOpen && (
                <div className="absolute mt-1 w-full bg-white border border-gray-200 rounded-md shadow-lg max-h-80 overflow-y-auto z-[9999]">
                  <div className="py-1">
                    <button
                      onClick={() => handleSegmentSelect('All')}
                      className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-100 ${activeSegment === 'All' ? 'bg-blue-50 text-blue-600' : 'text-gray-700'
                        }`}
                    >
                      All
                    </button>

                    {/* Segment options with counts */}
                    {displayedSegments.map((seg) => (
                      <button
                        key={seg.id}
                        onClick={() => handleSegmentSelect(seg.name)}
                        className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-100 ${activeSegment === seg.name ? 'bg-blue-50 text-blue-600' : 'text-gray-700'
                          }`}
                      >
                        <span className="flex items-center justify-between w-full">
                          <span>{seg.name}</span>
                          <span className="text-xs text-gray-500 ml-2">
                            {seg.loadingCount ? (
                              <span className="inline-block w-4 h-4 border-2 border-t-transparent border-gray-400 rounded-full animate-spin"></span>
                            ) : (
                              `(${seg.memberCount ?? 0})`
                            )}
                          </span>
                        </span>
                      </button>
                    ))}

                    {uniqueSegments.length > 6 && (
                      <button
                        onClick={handleToggleSegments}
                        className="w-full text-left px-3 py-2 text-sm text-blue-600 hover:bg-gray-100 border-t border-gray-200 font-medium"
                      >
                        {hasMoreSegments ? 'Show More' : 'Show Less'}
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* ⭐ Refresh button */}
            {/* <button
              onClick={handleRefresh}
              disabled={isLoading || isRefreshing || loadingSegments}
              className={`p-2 text-gray-500 hover:text-teal-600 transition-colors ${isLoading || isRefreshing || loadingSegments ? 'opacity-50 cursor-not-allowed' : ''
                }`}
              title="Refresh data"
            >
              <FiRefreshCwComponent
                size={18}
                className={isLoading || isRefreshing || loadingSegments ? 'animate-spin' : ''}
              />
            </button> */}
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={handleRefresh}
              disabled={isLoading || isRefreshing || loadingSegments}
              className={`p-2 text-gray-500 hover:text-teal-600 transition-colors ${isLoading || isRefreshing || loadingSegments ? 'opacity-50 cursor-not-allowed' : ''
                }`}
              title="Refresh data"
            >
              <FiRefreshCwComponent
                size={18}
                className={isLoading || isRefreshing || loadingSegments ? 'animate-spin' : ''}
              />
            </button>
            <button
              disabled={selected.length === 0}
              onClick={() => setIsChangeOpen(true)}
              className={`inline-flex items-center gap-2 px-3 py-2 rounded-md border text-sm ${selected.length === 0 ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-50'}`}
            >
              <FiRepeatComponent /> Change Segment
            </button>

            {/* Export All Button - Only show when "All" is selected */}
            {activeSegment === 'All' && (
              <button
                onClick={handleExportAllClick}
                disabled={totalCount === 0}
                className={`px-4 py-2 rounded-sm text-sm font-medium flex items-center ${totalCount === 0
                  ? 'opacity-50 cursor-not-allowed bg-gray-300'
                  : 'bg-orange-500 text-white hover:bg-orange-600'
                  }`}
              >
                Export All
              </button>
            )}

            {/* Export Segment Button - Only show when a specific segment is selected */}
            {activeSegment !== 'All' && (
              <button
                onClick={handleExportSegmentClick}
                disabled={segmentTotalCount === 0}
                className={`px-4 py-2 rounded-sm text-sm font-medium flex items-center ${segmentTotalCount === 0
                  ? 'opacity-50 cursor-not-allowed bg-gray-300'
                  : 'bg-orange-500 text-white hover:bg-orange-600'
                  }`}
              >
                Export Segment
              </button>
            )}
          </div>
        </div>

        <div className="px-6">
          <AudienceTable
            paginatedAudience={getPaginatedData}
            selected={selected}
            toggleSelect={(id) => setSelected(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id])}
            toggleSelectAll={(e) => e.target.checked ? setSelected(getPaginatedData.map(a => a.id)) : setSelected([])}
            requestSort={(key) => {
              let direction: 'asc' | 'desc' = 'asc';
              if (sortConfig?.key === key && sortConfig.direction === 'asc') direction = 'desc';
              setSortConfig({ key, direction });
            }}
            getSortIcon={(key) => (sortConfig?.key === key ? (sortConfig.direction === 'asc' ? <FiChevronUpComponent size={14} /> : <FiChevronDownComponent size={14} />) : <FiChevronDownComponent size={14} />)}
            getStatusColor={getStatusColor}
            fieldConfigs={fieldConfigs}
            nameFilter={nameFilter}
            onNameFilterApply={handleNameFilterApply}
            onNameFilterClear={handleNameFilterClear}
            setSortConfig={setSortConfig}
            onContactDeleted={refreshContactsWithDelay}
            loading={isLoading}
            showCheckboxes={showCheckboxes}
          />
        </div>

        {/* Modern Pagination - Same style as TemplatesGallery */}
        {showPagination && (
          <div className='flex items-center justify-between px-6 py-4 border-t border-gray-200 bg-white rounded-b-md'>
            <span className='text-sm text-gray-500'>
              Showing {startItem} to {endItem} of {getTotalCount} contacts {activeSegment !== 'All' && `in "${activeSegment}"`}
            </span>

            <div className="flex items-center justify-center gap-2">
              <button
                onClick={() => handlePageChange(currentPageDisplay - 1)}
                disabled={currentPageDisplay === 0}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>

              {(() => {
                const pages = [];
                const total = totalPagesDisplay;
                const current = currentPageDisplay + 1;
                const delta = 1;

                pages.push(1);

                let rangeStart = Math.max(2, current - delta);
                let rangeEnd = Math.min(total - 1, current + delta);

                if (rangeStart > 2) {
                  pages.push('...');
                }

                for (let i = rangeStart; i <= rangeEnd; i++) {
                  pages.push(i);
                }

                if (rangeEnd < total - 1) {
                  pages.push('...');
                }

                if (total > 1) {
                  pages.push(total);
                }

                return pages.map((page, index) => {
                  if (page === '...') {
                    return (
                      <span
                        key={`ellipsis-${index}`}
                        className="px-3 py-2 text-sm text-gray-500"
                      >
                        …
                      </span>
                    );
                  }

                  const pageNumber = page as number;
                  const isActive = pageNumber === current;

                  return (
                    <button
                      key={pageNumber}
                      onClick={() => handlePageChange(pageNumber - 1)}
                      className={`px-4 py-2 text-sm font-medium rounded-lg ${isActive
                        ? 'bg-orange-500 text-white'
                        : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50'
                        }`}
                    >
                      {pageNumber}
                    </button>
                  );
                });
              })()}

              <button
                onClick={() => handlePageChange(currentPageDisplay + 1)}
                disabled={currentPageDisplay + 1 === totalPagesDisplay}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      <ImportAudienceModal
        isOpen={isImportOpen}
        onClose={() => setIsImportOpen(false)}
        onSuccess={() => {
          lastFetchedPage.current = -1;
          fetchContacts(currentPage);
          if (activeSegment !== 'All' && activeSegmentId) {
            fetchSegmentMembers(activeSegmentId);
          }
          // Refresh segment counts after a 1-second delay
          setTimeout(() => {
            // Reset segment counts and fetch them again
            setApiSegments(prevSegments =>
              prevSegments.map(seg => ({
                ...seg,
                memberCount: undefined,
                loadingCount: true
              }))
            );
            // Fetch updated counts for all segments
            apiSegments.forEach(seg => {
              fetchSegmentCount(seg.id);
            });
          }, 1000);
        }}
      />
      <ChangeSegmentModal
        isOpen={isChangeOpen}
        onClose={() => setIsChangeOpen(false)}
        selectedCount={selected.length}
        selectedIds={selected}
        onSuccess={() => {
          setSelected([]);
          lastFetchedPage.current = -1;
          fetchContacts(currentPage);
          if (activeSegment !== 'All' && activeSegmentId) {
            fetchSegmentMembers(activeSegmentId);
          }
        }}
        onSegmentCountRefresh={() => {
          // Refresh segment counts after a 1-second delay
          // Reset segment counts and fetch them again
          setApiSegments(prevSegments =>
            prevSegments.map(seg => ({
              ...seg,
              memberCount: undefined,
              loadingCount: true
            }))
          );
          // Fetch updated counts for all segments
          apiSegments.forEach(seg => {
            fetchSegmentCount(seg.id);
          });
        }}
      />
      <EditTableFormatModal
        isOpen={isEditOpen}
        onClose={() => setIsEditOpen(false)}
        fields={tableFields}
        initialFields={tableFields}
        onSave={handleSaveFields}
      />

      {/* Export Modal */}
      <ExportModal
        isOpen={isExportModalOpen}
        onClose={() => {
          setIsExportModalOpen(false);
          setPendingExportType(null);
        }}
        onExport={handleExportConfirm}
        onSnapshot={handleSnapshot}
        loading={isCreatingSnapshot}
        isAllSelected={activeSegment === 'All'}
      />

      {/* Snapshot Status Modal */}
      <SnapshotStatusModal
        isOpen={isSnapshotStatusModalOpen}
        onClose={() => {
          setIsSnapshotStatusModalOpen(false);
          setCurrentSnapshotId(null);
          setCurrentSegmentId(null);
        }}
        segmentId={currentSegmentId || ''}
        snapshotId={currentSnapshotId || ''}
      />

      {/* Export Preview Modal */}
      <ExportPreviewModal
        isOpen={isExportPreviewModalOpen}
        onClose={() => {
          setIsExportPreviewModalOpen(false);
          setCurrentExportId(null);
          setCurrentSnapshotId(null);
          setCurrentSegmentId(null);
        }}
        exportId={currentExportId || ''}
        snapshotId={currentSnapshotId || ''}
        segmentId={currentSegmentId || ''}
        onDownload={handleDownloadExport}
      />

      <ToastContainer position="top-right" autoClose={3000} />
    </div>
  );
};

export default Audience;